import { apiClient, getAccessToken, refreshAccessToken } from './client';
import type { WallFrame } from '../live/liveWallModel';

/** A case streaming right now for the tenant. */
export interface LiveCase {
  case_id: string;
  procedure_id: string | null;
  /** Participant (user) ids streaming in — resolve to names client-side. */
  participant_ids: string[];
  /**
   * Set on a wall someone was split out onto (LIVE-SPLIT-PARTICIPANT): the
   * case id they were split out of. The live case is inferred as
   * tenant + procedure, so a simulation running alongside a real case is
   * overlaid with it; splitting is how an operator separates them mid-session.
   */
  split_of?: string | null;
  /** On a split wall, the one participant streaming there. */
  split_participant_id?: string | null;
}

/**
 * A live case the logged-in participant is streaming into
 * (LIVE-WALL-PARTICIPANT). Carries what an admin resolves from tenant lists
 * a participant can't read: the tenant, the procedure's name, and the
 * teammates' names (names only).
 */
export interface MyLiveCase extends LiveCase {
  tenant_id: string;
  tenant_name: string;
  procedure_name: string | null;
  participant_names: Record<string, string>;
}

/** The cases the logged-in participant is part of right now — the whole
 *  team's wall, read-only. Never another case in their tenant. */
export async function listMyLiveCases(): Promise<MyLiveCase[]> {
  const { data } = await apiClient.get<MyLiveCase[]>('/api/v1/me/live-wall/cases');
  return data;
}

/** The cases the tenant has live at this moment. */
export async function listLiveCases(tenantId: string): Promise<LiveCase[]> {
  const { data } = await apiClient.get<LiveCase[]>(
    `/api/v1/tenants/${tenantId}/live-wall/cases`,
  );
  return data;
}

/**
 * Move a participant off a shared wall onto their own. Takes effect from
 * their next sample; what they contributed to the shared wall is dropped so
 * it stops drawing a line for someone who is no longer streaming there.
 * Returns the case id of the wall they now stream to.
 */
export async function splitLiveParticipant(
  tenantId: string,
  caseId: string,
  participantId: string,
): Promise<LiveCase['case_id']> {
  const { data } = await apiClient.post<{ case_id: string }>(
    `/api/v1/tenants/${tenantId}/live-wall/${encodeURIComponent(
      caseId,
    )}/split/${encodeURIComponent(participantId)}`,
  );
  return data.case_id;
}

/** Undo a split — the participant's next samples land back on the shared
 *  wall and their split wall is torn down. Returns the shared case id. */
export async function mergeLiveParticipant(
  tenantId: string,
  caseId: string,
  participantId: string,
): Promise<LiveCase['case_id']> {
  const { data } = await apiClient.delete<{ case_id: string }>(
    `/api/v1/tenants/${tenantId}/live-wall/${encodeURIComponent(
      caseId,
    )}/split/${encodeURIComponent(participantId)}`,
  );
  return data.case_id;
}

/**
 * Live Recording live stream (LIVE-OR-STREAMING).
 *
 * Subscribes to a case's SSE frame stream and hands each parsed frame back.
 * Uses fetch + ReadableStream (not native EventSource) to match the existing
 * live-view client — same reconnect discipline. Points at the dev-harness
 * stream for now; the production tenant-scoped, token-authed endpoint is a
 * small follow-up (same frame shape, so nothing here changes but the URL/auth).
 */

export interface LiveWallHandlers {
  onFrame: (frame: WallFrame) => void;
  onReady?: () => void;
  onError?: () => void;
  /** The server signalled the session finished (phones stopped posting). */
  onEnded?: () => void;
}

interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse buffered SSE text into complete events, returning the leftover partial
 * buffer. Pure — the streaming wrapper feeds it decoded chunks. `:`-comment
 * lines (keepalive pings) are ignored.
 */
export function parseSseEvents(buf: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  let nl: number;
  while ((nl = buf.indexOf('\n\n')) >= 0) {
    const raw = buf.slice(0, nl);
    buf = buf.slice(nl + 2);
    let event = 'message';
    let data = '';
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (data || event !== 'message') events.push({ event, data });
  }
  return { events, rest: buf };
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/** Open the tenant-scoped, authed stream; returns a stop function. Reconnects
 *  on drop (refreshing the token on a 401) until stopped. */
export function streamLiveWall(
  tenantId: string,
  caseId: string,
  handlers: LiveWallHandlers,
): () => void {
  return streamFrom(
    `/api/v1/tenants/${tenantId}/live-wall/${encodeURIComponent(caseId)}/stream`,
    handlers,
  );
}

/** The participant's stream of a case they are in — same protocol, scoped to
 *  the caller server-side. */
export function streamMyLiveWall(
  caseId: string,
  handlers: LiveWallHandlers,
): () => void {
  return streamFrom(
    `/api/v1/me/live-wall/${encodeURIComponent(caseId)}/stream`,
    handlers,
  );
}

function streamFrom(path: string, handlers: LiveWallHandlers): () => void {
  let stopped = false;
  let ended = false;
  let ctrl: AbortController | null = null;
  const base = apiClient.defaults.baseURL ?? '';
  const urlFor = (token: string | null): string =>
    `${base}${path}?token=${encodeURIComponent(token ?? '')}`;

  async function run(): Promise<void> {
    while (!stopped && !ended) {
      ctrl = new AbortController();
      try {
        let res = await fetch(urlFor(getAccessToken()), {
          signal: ctrl.signal,
          credentials: 'include',
          headers: { Accept: 'text/event-stream' },
        });
        if (res.status === 401) {
          const fresh = await refreshAccessToken();
          if (stopped) break;
          res = await fetch(urlFor(fresh), {
            signal: ctrl.signal,
            credentials: 'include',
            headers: { Accept: 'text/event-stream' },
          });
        }
        if (!res.ok || !res.body) {
          if (!stopped) handlers.onError?.();
        } else {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const { events, rest } = parseSseEvents(buf);
            buf = rest;
            for (const e of events) {
              if (e.event === 'ready') handlers.onReady?.();
              else if (e.event === 'ended') {
                ended = true;
                handlers.onEnded?.();
              } else if (e.event === 'frame') {
                try {
                  handlers.onFrame(JSON.parse(e.data) as WallFrame);
                } catch {
                  /* skip a malformed frame */
                }
              }
            }
            if (ended) break;
          }
        }
      } catch {
        if (!stopped) handlers.onError?.();
      }
      if (!stopped && !ended) await delay(1500); // reconnect after a drop
    }
  }

  void run();
  return () => {
    stopped = true;
    ctrl?.abort();
  };
}
